export type OrderEndOutcome = 'success' | 'fail';

interface PaymentFlowData {
    success?: boolean;
    isAutoRefresh?: boolean; // the server marks a payment that is still being confirmed with this
    cartId?: unknown;
}

// The route's cart-id slot can hold the 32-character payment secret instead of a cart number,
// so only plain digits count: Number("12e5...") or parseInt("3f9a...") would give a wrong id.
const asCartId = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? value : null;
    if (typeof value === 'string' && /^[1-9]\d{0,14}$/.test(value)) return Number(value);
    return null;
};

/**
 * What the order-end analytics event should say for one payment-status answer.
 * A payment that is still pending has no outcome yet, so nothing is reported for it.
 * The cart id is the one in the page address, else the one the server returned, never NaN.
 */
export const getOrderEndOutcome = (
    data: PaymentFlowData | null | undefined,
    routeCartId?: string | null
): { outcome: OrderEndOutcome; cartId: number } | null => {
    if (!data) return null;
    if (!data.success && data.isAutoRefresh) return null;

    const cartId = asCartId(routeCartId) ?? asCartId(data.cartId);
    if (cartId === null) return null;

    return { outcome: data.success ? 'success' : 'fail', cartId };
};

/**
 * Reports each outcome at most once. The status page asks the server again every few seconds,
 * so without this every poll would report again.
 */
export const createOrderEndReporter = (report: (outcome: OrderEndOutcome, cartId: number) => void) => {
    const reported = new Set<OrderEndOutcome>();

    return (data: PaymentFlowData | null | undefined, routeCartId?: string | null): void => {
        const result = getOrderEndOutcome(data, routeCartId);
        if (!result || reported.has(result.outcome)) return;
        reported.add(result.outcome);
        report(result.outcome, result.cartId);
    };
};
